import React from "react";
import { Button, Modal, Font } from "@moneyforward/mfx_biz_react_components";
import { css } from "@emotion/react";
import { BizsolDisconnectStepper } from "@/components/atoms/BizsolDisconnectStepper";
import { color } from "@/styles/theme";
import { PasswordFormField, UsernameLabel } from "@/components/molecules";
import { passwordValidation } from "@/components/validations";
import { useUpdateTemporaryPasswordMutation } from "@/generated/graphql";
import { moveToErrorPage } from "@/libs/errors";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import { bizsolDisconnectModalState } from "@/store/bizsolDisconnectModalState";
import useRollbarUtil from "../hooks/useRollbarUtil";

type Props = {
  title: string;
  sub: string;
  username: string;
  onClickCancel: () => void;
};

type FormValues = {
  password: string;
};

export const BizsolDisconnectModalContentStep2 = ({
  title,
  sub,
  username,
  onClickCancel,
}: Props) => {
  const { rollbar } = useRollbarUtil();
  const nextStep = 3;
  const [disconnectState, setDisconnectState] = useRecoilState(
    bizsolDisconnectModalState
  );

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      password: disconnectState.temporaryPassword,
    },
  });
  const { handleSubmit, formState } = methods;

  const [updateTemporaryPasswordMutation] = useUpdateTemporaryPasswordMutation({
    onCompleted: (data) => {
      if (data.updateTemporaryPassword.result) {
        setDisconnectState({
          step: nextStep,
          temporaryPassword: methods.getValues().password,
        });
      } else {
        moveToErrorPage();
      }
    },
    onError: (err) => {
      rollbar.error("Failed to update temporary password", err);
      moveToErrorPage();
    },
  });

  const onSubmit = async (formValues: FormValues) => {
    await updateTemporaryPasswordMutation({
      variables: {
        input: {
          sub: sub,
          password: formValues.password,
        },
      },
    });
  };

  const body = (
    <div>
      <BizsolDisconnectStepper step={2} />
      <Font variant="l" component="p">
        仮パスワードを発行してください。
      </Font>
      <div css={container}>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div css={inputWrapper}>
              <UsernameLabel username={username} label={"Mikatano ID"} />
              <PasswordFormField
                name="password"
                usePasswordMeter={true}
                requiredLabel={false}
                registerOptions={{
                  validate: passwordValidation,
                }}
                isTemporary={true}
              />
            </div>
            <div css={buttonWrapper}>
              <Button
                type="submit"
                variant="primary"
                disabled={formState.isSubmitting}
              >
                仮パスワード発行
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );

  return (
    <Modal
      css={modal}
      title={title}
      body={body}
      zIndex={5}
      onClose={onClickCancel}
      backgroundColor={color.backgroundGray}
    />
  );
};

const modal = css({
  width: "614px",
});

const container = css({
  marginTop: "16px",
});

const inputWrapper = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const buttonWrapper = css({
  marginTop: "24px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  gap: "16px",
});
